import { useCallback, useEffect, useMemo, useState } from 'react';

import orderBy from 'lodash.orderby';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';

import { useCustomer } from '@techstyle/react-accounts';
import { useIntl, defineMessages } from '@techstyle/react-intl';
import { useDomain } from '@techstyle/redux-core';

import formatProductUrl from './formatProductUrl';
import { useLDFlags } from './LD/useLDFlags';
import {
  getClosestModelBySize,
  getModelSizeIndex,
  getSelectedSize,
} from './model';
import { getProductModelImages } from './productDetail';
import { isSoldOut as getIsSoldOut } from './selectors';
import { convertToEULength, translateToEUSize } from './sizes';

const MODEL = 'model';

const messages = defineMessages({
  model_height_measurement: {
    id: 'site_pdp.model_height_measurement',
    defaultMessage: "{name} - {height}'' size {size}",
  },
  model_height_measurement_eu: {
    id: 'site_pdp.model_height_measurement_eu',
    defaultMessage: '{name} - {height} cm size {size}',
  },
  model_name: {
    id: 'site_pdp.model_name',
    defaultMessage: 'Model',
  },
});

const useOrderedModels = (
  product = {},
  isCurvy = false,
  isProductSet = false,
  isOnGrid = false
) => {
  const [isLinkedProductRedirected, setIsLinkedProductRedirected] =
    useState(false);
  const [{ initialModelId, isInitialModelLinkedProduct }, setinitialModel] =
    useState({
      initialModelId: null,
      isInitialModelLinkedProduct: false,
    });
  const intl = useIntl();
  const { tld } = useDomain();
  const { 'match-user-size-with-model-size': isMatchUserSizeFeatureEnabled } =
    useLDFlags();
  const customer = useCustomer();
  const { isSoldOut } = useSelector(state => ({
    isSoldOut: getIsSoldOut(state, { product }),
  }));
  const router = useRouter();
  const { query, replace } = router || {};

  const selectedSize = !isProductSet
    ? getSelectedSize(product, customer, isSoldOut)
    : {};
  const { value: selectedSizeValue } = selectedSize;

  const translateModelName = useCallback(
    name =>
      name && name.toLowerCase() !== MODEL
        ? name
        : intl.formatMessage(messages.model_name),
    [intl]
  );

  /*
   * Since EU and UK share the same key in RB, we need to create a new RB specifically for .eu
   * because they use centimeter as a unit. While UK uses inches.
   */
  const parseModelDescription = useCallback(
    (modelJsonFormat, product) =>
      intl.formatMessage(
        tld === '.eu'
          ? messages.model_height_measurement_eu
          : messages.model_height_measurement,
        {
          name: translateModelName(modelJsonFormat.name),
          height:
            tld === '.com' || tld === '.co.uk'
              ? modelJsonFormat.height
              : intl.formatNumber(
                  convertToEULength(modelJsonFormat.height, tld),
                  {
                    maximumFractionDigits: tld === '.de' ? 2 : 0,
                  }
                ),
          size: translateToEUSize(
            product.product_category_id_list,
            product.tag_id_list,
            modelJsonFormat.size,
            tld
          ),
        }
      ),
    [intl, tld, translateModelName]
  );

  const modelSizeIndex = useCallback(
    (modelSize, product) => {
      if (isMatchUserSizeFeatureEnabled) {
        return getModelSizeIndex(modelSize, product, tld);
      }
      return {};
    },
    [isMatchUserSizeFeatureEnabled, tld]
  );

  const { orderedModels, modelList } = useMemo(() => {
    const modelList = getProductModelImages(product, !isOnGrid);
    const options = modelList.reduce((filtered, model) => {
      model.isDefaultModel = false;
      model.isMemberModel = false;

      const { vimeo_id: vimeoId } = model.product;
      const modelAngles = [...model.angles];

      if (vimeoId) {
        modelAngles.splice(modelAngles.length - 1, 0, 'video');
      }

      model.angles = modelAngles;
      if (model.jsonFormat) {
        const modelJson = model.jsonFormat[0] || model.jsonFormat;
        const defaultAngle = modelJson.angle?.toString();
        const modelIndex = parseInt(modelJson.modelIndex, 10);
        const isDefaultAngleAvailable = modelAngles.includes(defaultAngle);

        if (model.isCombinedModel) {
          const description = [
            parseModelDescription(modelJson, model.product),
            parseModelDescription(model.jsonFormat[1], model.product),
          ];
          model.description = description.join('|');
        } else {
          model.description = parseModelDescription(modelJson, model.product);
        }

        if (isMatchUserSizeFeatureEnabled) {
          model.canBeDefault = modelJson.default !== 0;
        }

        if (isDefaultAngleAvailable) {
          model.defaultAngle = defaultAngle;
        }

        if (modelIndex >= 0) {
          model.modelIndex = modelIndex;
        }
      }

      if (model.description) {
        if (model.isCombinedModel) {
          // get model size separately for ambassador and member models from combined description
          const combinedDescriptions = model.description
            .split('|')
            .map(description => description.trim());
          const ambassadorDescArr = combinedDescriptions[0].split(' ');
          const memberModelDescArr = combinedDescriptions[1].split(' ');
          const ambassadorModelSize =
            ambassadorDescArr[ambassadorDescArr.length - 1];
          const memberModelSize =
            memberModelDescArr[memberModelDescArr.length - 1];
          // for combined model, separate it into 2 models to render 2 thumbnails
          filtered.push(
            {
              ...model,
              isAmbassadorModel: true,
              thumbnailAngleIndex: model.defaultAngle || '1',
              modelInfo: combinedDescriptions[0],
              modelSize: ambassadorModelSize,
              isPlusSize: isCurvy
                ? !model.isLinkedProduct
                : model.isLinkedProduct,
              ...modelSizeIndex(ambassadorModelSize, product),
            },
            {
              ...model,
              isAmbassadorModel: false,
              modelInfo: combinedDescriptions[1],
              modelSize: memberModelSize,
              thumbnailAngleIndex: model.defaultAngle || '2',
              isPlusSize: isCurvy
                ? !model.isLinkedProduct
                : model.isLinkedProduct,
              ...modelSizeIndex(memberModelSize, product),
            }
          );
        } else {
          // default
          const modelDescArr = model.description.split(' ');
          const modelSize = modelDescArr[modelDescArr.length - 1];
          filtered.push({
            ...model,
            isAmbassadorModel: false,
            modelInfo: model.description,
            modelSize: modelSize,
            thumbnailAngleIndex: model.defaultAngle || '1',
            isPlusSize: isCurvy
              ? !model.isLinkedProduct
              : model.isLinkedProduct,
            ...modelSizeIndex(modelSize, product),
          });
        }
      }
      return filtered;
    }, []);

    if (isMatchUserSizeFeatureEnabled) {
      return {
        orderedModels: options.sort(
          (modelA, modelB) => modelA.sizeIndex - modelB.sizeIndex
        ),
        modelList,
      };
    }

    return { orderedModels: orderBy(options, ['isPlusSize']), modelList };
  }, [
    isCurvy,
    isMatchUserSizeFeatureEnabled,
    isOnGrid,
    modelSizeIndex,
    parseModelDescription,
    product,
  ]);

  const closestModelBySize = useCallback(
    (models, newSize) => {
      const { sizeIndex: selectedSizeIndex } = modelSizeIndex(
        newSize || selectedSizeValue,
        product
      );
      const closest = getClosestModelBySize(
        models,
        selectedSizeIndex,
        models[0]
      );
      return closest;
    },
    [modelSizeIndex, product, selectedSizeValue]
  );

  // Set the initial model ID whenever it is not defined
  useEffect(() => {
    const model =
      !isMatchUserSizeFeatureEnabled || !selectedSizeValue
        ? modelList[0]
        : closestModelBySize(orderedModels);

    if (model && !initialModelId) {
      setinitialModel({
        initialModelId: model.modelId,
        isInitialModelLinkedProduct: model.isLinkedProduct,
      });
    }
  }, [
    closestModelBySize,
    initialModelId,
    isMatchUserSizeFeatureEnabled,
    modelList,
    orderedModels,
    selectedSizeValue,
  ]);

  const initialModel = useMemo(() => {
    return (
      orderedModels.find(model => model.modelId === initialModelId) ||
      modelList[0]
    );
  }, [initialModelId, modelList, orderedModels]);

  /*
   * Redirect the customer to the model's product if it is a linked product
   * so that the model angles will be loaded. This only happens once
   */
  useEffect(() => {
    const { master_product_id: masterProductId, permalink } =
      initialModel?.product || {};

    if (
      isMatchUserSizeFeatureEnabled &&
      !isLinkedProductRedirected &&
      isInitialModelLinkedProduct &&
      !isOnGrid &&
      initialModel?.isLinkedProduct &&
      initialModelId
    ) {
      const { as, href } = formatProductUrl({
        extraQueryParams: {
          psrc: query.psrc,
        },
        permalink: permalink,
        productId: masterProductId,
      });
      setIsLinkedProductRedirected(true);
      replace(href, as);
    }
  }, [
    initialModel,
    initialModelId,
    isLinkedProductRedirected,
    isInitialModelLinkedProduct,
    isMatchUserSizeFeatureEnabled,
    isOnGrid,
    query,
    replace,
  ]);

  return {
    closestModelBySize,
    initialModel,
    initialModelId,
    isMatchUserSizeFeatureEnabled,
    modelList,
    orderedModels,
  };
};

export default useOrderedModels;
